import type { ReactNode, SVGProps } from "react"

/* ─────────────────────────────────────────────────────────────────────────────
 * Biblioteca de icones.
 *
 * No prototipo cada icone era um <svg> inline, reescrito onde aparecia — a mesma
 * seta existia em cinco arquivos, com tres geometrias ligeiramente diferentes.
 * Aqui cada forma existe uma vez.
 *
 * Convencoes:
 *   • O `viewBox` de cada icone e o nativo do desenho. `size` so escala o
 *     render, entao o mesmo icone serve a 12, 14 ou 28px sem redesenhar.
 *   • Traço em `currentColor` por padrao — a cor vem do container, o que faz os
 *     estados de hover funcionarem sem prop de cor.
 *   • `aria-hidden` por padrao: icone acompanha texto. Quando ele for a unica
 *     coisa dentro de um botao, quem chama passa `aria-label` no botao.
 * ────────────────────────────────────────────────────────────────────────── */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "viewBox" | "width" | "height"> {
  size?: number
}

function Svg({ box, size, children, ...rest }: IconProps & { box: number; size: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox={`0 0 ${box} ${box}`} fill="none" aria-hidden="true" {...rest}>
      {children}
    </svg>
  )
}

/* ── Navegacao e acoes ────────────────────────────────────────────────────── */

export function ArrowRight({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <path
        d="M3 8h10m-4.5-4L13 8l-4.5 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

/** Variante optica para 14px, usada em links de texto e botoes compactos. */
export function ArrowRightSm({ size = 14, ...rest }: IconProps) {
  return (
    <Svg box={14} size={size} {...rest}>
      <path
        d="M2.5 7h9m-4-4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function Menu({ size = 22, ...rest }: IconProps) {
  return (
    <Svg box={22} size={size} {...rest}>
      <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Svg>
  )
}

export function Close({ size = 18, ...rest }: IconProps) {
  return (
    <Svg box={18} size={size} {...rest}>
      <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Svg>
  )
}

export function Plus({ size = 12, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

export function ChevronDown({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function Download({ size = 14, ...rest }: IconProps) {
  return (
    <Svg box={14} size={size} {...rest}>
      <path
        d="M7 1v7.5M4.5 6.5L7 9l2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M2 10v1a1 1 0 001 1h8a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}

export function ListRows({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <path d="M2 4h12M2 8h12M2 12h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}

export function InfoCircle({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.5v3.5M8 5.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

/* ── Estados de cobertura (cards de plano) ────────────────────────────────── */

export function CheckCircle({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <circle cx="8" cy="8" r="7" fill="#2E3724" fillOpacity="0.08" />
      <path d="M5 8l2 2 4-4" stroke="#405439" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function CrossCircle({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <circle cx="8" cy="8" r="7" fill="#C9963B" fillOpacity="0.08" />
      <path d="M6 6l4 4M10 6l-4 4" stroke="#C9963B" strokeWidth="1.3" strokeLinecap="round" />
    </Svg>
  )
}

export function StarFilled({ size = 12, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <path d="M6 1l1.2 3.5H11L8.2 6.8l1.2 3.5L6 8.5l-3.4 1.8 1.2-3.5L1 4.5h3.8z" fill="#D8B36A" />
    </Svg>
  )
}

/* ── Contato e localizacao ────────────────────────────────────────────────── */

export function MapPinSm({ size = 12, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <path
        d="M6 1.5C4.07 1.5 2.5 3.07 2.5 5c0 2.5 3.5 5.5 3.5 5.5S9.5 7.5 9.5 5C9.5 3.07 7.93 1.5 6 1.5z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="6" cy="5" r="1.2" fill="currentColor" />
    </Svg>
  )
}

export function ClockSm({ size = 12, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 3.5v2.5l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </Svg>
  )
}

export function GlobeSm({ size = 12, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M3 6h6M6 3c-1 1.5-1 4.5 0 6M6 3c1 1.5 1 4.5 0 6"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function MailSm({ size = 11, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <rect x="1.5" y="3" width="9" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 4l4.5 3 4.5-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </Svg>
  )
}

export function PhoneSm({ size = 11, ...rest }: IconProps) {
  return (
    <Svg box={12} size={size} {...rest}>
      <path
        d="M10.5 7.5c-.7-.1-1.4-.2-2-.4-.2-.1-.5 0-.6.2l-.9 1.1C5.8 7.8 4.2 6.2 3.6 4.9l1.1-.9c.2-.1.3-.4.2-.6C4.7 2.8 4.6 2.1 4.5 1.5 4.4 1.2 4.1 1 3.8 1H2C1.7 1 1.5 1.2 1.5 1.5c0 5 4 9 9 9 .3 0 .5-.2.5-.5V8.2c0-.3-.2-.6-.5-.7z"
        fill="currentColor"
      />
    </Svg>
  )
}

/** Pin grande dos estados vazios da busca de rede. */
export function MapPinLg({ size = 24, ...rest }: IconProps) {
  return (
    <Svg box={24} size={size} {...rest}>
      <path
        d="M12 4C8.13 4 5 7.13 5 11c0 6 7 13 7 13s7-7 7-13c0-3.87-3.13-7-7-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    </Svg>
  )
}

/* ── Indicadores do hero (18px) ───────────────────────────────────────────── */

export function ClockBadge({ size = 18, ...rest }: IconProps) {
  return (
    <Svg box={18} size={size} {...rest}>
      <path d="M9 2a5 5 0 100 10A5 5 0 009 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M9 5.5v2.5l1.5 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Svg>
  )
}

export function Paws({ size = 18, ...rest }: IconProps) {
  return (
    <Svg box={18} size={size} {...rest}>
      <ellipse cx="6.5" cy="11" rx="3.5" ry="4.5" stroke="currentColor" strokeWidth="1.4" />
      <ellipse cx="11.5" cy="11" rx="3.5" ry="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M6.5 3C6.5 3 5 5 5 6.5M11.5 3C11.5 3 13 5 13 6.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </Svg>
  )
}

export function MapPinBadge({ size = 18, ...rest }: IconProps) {
  return (
    <Svg box={18} size={size} {...rest}>
      <path
        d="M9 2C6.24 2 4 4.24 4 7c0 4.5 5 9 5 9s5-4.5 5-9c0-2.76-2.24-5-5-5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.4" />
    </Svg>
  )
}

export function HeartFilled({ size = 16, ...rest }: IconProps) {
  return (
    <Svg box={16} size={size} {...rest}>
      <path
        d="M8 2C5.24 2 3 4.24 3 7c0 1.8.88 3.4 2.24 4.38L8 13l2.76-1.62A5 5 0 0013 7c0-2.76-2.24-5-5-5z"
        fill="currentColor"
      />
    </Svg>
  )
}

export function StarOutline({ size = 15, ...rest }: IconProps) {
  return (
    <Svg box={15} size={size} {...rest}>
      <path
        d="M7.5 1.5l1.5 3.5 3.5.5-2.5 2.5.5 3.5-3-1.5-3 1.5.5-3.5L2.5 5.5l3.5-.5z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

/* ── Pilares da marca (24px, faixa de confianca) ──────────────────────────── */

export function ShieldCare({ size = 24, ...rest }: IconProps) {
  return (
    <Svg box={24} size={size} {...rest}>
      <path
        d="M12 3C8.5 3 5.5 6 5.5 9.5c0 2.7 1.5 5.1 3.8 6.3L12 17l2.7-1.2C16.9 14.6 18.5 12.2 18.5 9.5 18.5 6 15.5 3 12 3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M9 9.5h6M12 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

export function Star({ size = 24, ...rest }: IconProps) {
  return (
    <Svg box={24} size={size} {...rest}>
      <path
        d="M12 3l1.5 4.5H18l-3.75 2.7 1.5 4.5L12 12l-3.75 2.7 1.5-4.5L6 7.5h4.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function HeartDouble({ size = 24, ...rest }: IconProps) {
  return (
    <Svg box={24} size={size} {...rest}>
      <path
        d="M12 21C12 21 4 16 4 10a4 4 0 0 1 8 0 4 4 0 0 1 8 0c0 6-8 11-8 11z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function TargetPlus({ size = 24, ...rest }: IconProps) {
  return (
    <Svg box={24} size={size} {...rest}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

/* ── Diferenciais e passos (28px) ─────────────────────────────────────────── */

export function HeartCare({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <path
        d="M14 4C10.13 4 7 7.13 7 11c0 2.97 1.5 5.55 3.8 6.83L14 19l3.2-1.17C19.5 16.55 21 13.97 21 11c0-3.87-3.13-7-7-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M10 11h8M14 7v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

export function StarLg({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <path
        d="M14 3l2 6h6l-5 3.6 2 6-5-3.6-5 3.6 2-6L6 9h6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function HeartDoubleLg({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <path
        d="M14 24S5 18 5 11a5 5 0 0 1 10 0 5 5 0 0 1 10 0c0 7-9 13-9 13H14z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function TargetLg({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <circle cx="14" cy="14" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="14" cy="14" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M14 5v4M14 19v4M5 14h4M19 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

export function PlanList({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <rect x="4" y="4" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 14h10M9 10h6M9 18h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
  )
}

export function PetRegister({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <circle cx="14" cy="10" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 23c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M19 12l2 2 4-4" stroke="#C9963B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  )
}

export function MapPinNetwork({ size = 28, ...rest }: IconProps) {
  return (
    <Svg box={28} size={size} {...rest}>
      <path
        d="M14 4C10.13 4 7 7.13 7 11c0 6 7 13 7 13s7-7 7-13c0-3.87-3.13-7-7-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="14" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    </Svg>
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Registro por nome.
 *
 * Os dados que descrevem secoes (diferenciais, passos, faixa de confianca) vem
 * dos services em JSON — e JSON nao carrega componente React. Cada item traz um
 * `icon: IconName`, e a resolucao acontece aqui. Quando esses dados passarem a
 * vir da API da BusiPet, o contrato ja e serializavel; nada muda.
 * ────────────────────────────────────────────────────────────────────────── */

export const ICONS = {
  "shield-care": ShieldCare,
  star: Star,
  "heart-double": HeartDouble,
  "target-plus": TargetPlus,
  "heart-care": HeartCare,
  "star-lg": StarLg,
  "heart-double-lg": HeartDoubleLg,
  "target-lg": TargetLg,
  "plan-list": PlanList,
  "pet-register": PetRegister,
  "map-pin-network": MapPinNetwork,
  "clock-badge": ClockBadge,
  paws: Paws,
  "map-pin-badge": MapPinBadge,
} as const

export type IconName = keyof typeof ICONS

/** Renderiza um icone a partir do nome vindo dos dados. */
export function Icon({ name, ...rest }: IconProps & { name: IconName }) {
  const Component = ICONS[name]
  return <Component {...rest} />
}
